/**
 * @file Helper for Slideshow
 */

import { np__, p__, __ } from '@@/bits/intl'

const COVER_PAGE_INDEX = 0

/**
 * Build a link to a slideshow
 * @param showLink - Link to the underlying padlet
 */
const buildSlideshowLink = (showLink: string, wishId?: number | string): string => {
  return `${showLink}/slideshow${wishId === undefined ? '' : `/wish/${wishId}`}`
}

const DEFAULT_QR_CODE_SIZE = 1059

const secondDurationLong = (number: number): string => {
  return np__(
    'Represents duration. Used for configuration. For example: 3 seconds',
    '%{number} second',
    '%{number} seconds',
    number,
    { number },
  )
}

const secondDurationShort = (number: number): string => {
  return p__('Represents duration. Used for configuration. For example: 3s', '%{number}s', { number })
}

const secondDurationText = (durationType: string): string => {
  if (durationType === 'auto') {
    return __('Auto')
  } else {
    return ''
  }
}

const AUTOPLAY_DURATION_TYPES = [1, 2, 3, 5, 15, 30, 60, 'auto'] as const
const AUTOPLAY_DURATIONS = AUTOPLAY_DURATION_TYPES.reduce((acc, duration) => {
  acc[duration] = {
    long: typeof duration === 'number' ? secondDurationLong(duration) : secondDurationText(duration),
    short: typeof duration === 'number' ? secondDurationShort(duration) : secondDurationText(duration),
  }
  return acc
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
}, {} as Record<typeof AUTOPLAY_DURATION_TYPES[number], { long: string; short: string }>)

type AutoplayDurationType = keyof typeof AUTOPLAY_DURATIONS

const MIN_SLIDE_DURATION_MS = 2000

const SLIDE_DURATION_PER_WORD_MS = 500
const SLIDE_DURATION_PER_ATTACHMENT_MS = 5000
const calculateAutoSlideDuration = (numWords: number, numAttachments: number): number => {
  return (
    MIN_SLIDE_DURATION_MS + SLIDE_DURATION_PER_WORD_MS * numAttachments + SLIDE_DURATION_PER_ATTACHMENT_MS * numWords
  )
}

const getCurrentPostCid = (entityId: string): string => `c${entityId}`

const NOTIFICATION_MESSAGES = {
  stoppedAutoplaying: __('Autoplay stopped'),
  startedAutoplayingForTouchDevice: __('Autoplay started, press pause to stop'),
  startedAutoplayingForMouseDevice: __('Autoplay started, press any key to stop'),
}

type EmbedSize = 'small' | 'medium' | 'large'
interface EmbedSizeOption {
  title: string
  width: number
  height: number
  key: EmbedSize
}

const embedSizeOptions: EmbedSizeOption[] = [
  {
    title: __('Small'),
    width: 480,
    height: 320,
    key: 'small',
  },
  {
    title: __('Medium'),
    width: 720,
    height: 480,
    key: 'medium',
  },
  {
    title: __('Large'),
    width: 1080,
    height: 720,
    key: 'large',
  },
]

export {
  AUTOPLAY_DURATIONS,
  AutoplayDurationType,
  COVER_PAGE_INDEX,
  DEFAULT_QR_CODE_SIZE,
  EmbedSize,
  EmbedSizeOption,
  NOTIFICATION_MESSAGES,
  buildSlideshowLink,
  calculateAutoSlideDuration,
  embedSizeOptions,
  getCurrentPostCid,
}
